import { API_PATH } from '../constants/api';
import { fetchApi } from '../http-request';
import { TParamsSendCurrentLocationDriver } from '../types/api/location';
import {
  PayloadAddSoToDo,
  TGetSoList,
  TParamsGetOrders,
  TPayloadCancelOrder,
  TPayloadPublishDeliveryNote,
} from '../types/api/mobi';
import { cleanEmptyKeyObject, cleanObject, isEmpty } from '../utils';

export const sendCurrentDriverLocation = async ({
  driverLogKey,
  payload,
}: TParamsSendCurrentLocationDriver) => {
  try {
    const response = await fetchApi.post(
      API_PATH.mobi.orderDriverLog,
      payload,
      {
        headers: {
          'X-Api-Key': driverLogKey,
        },
        withRequestId: true,
      }
    );
    const data = response?.data;
    if ([200, 201].indexOf(data.statusCode) === -1) {
      throw new Error(data.message);
    }
    return data?.data || null;
  } catch (error: unknown) {
    return Promise.reject(error);
  }
};

export const addSoToDo = async ({
  payload,
  params,
}: {
  payload: PayloadAddSoToDo;
  params: TGetSoList;
}) => {
  try {
    const cleanParams = cleanEmptyKeyObject(params);
    const urlParams = new URLSearchParams(cleanParams);
    urlParams.delete('customFieldFilters');

    if (!isEmpty(cleanParams.customFieldFilters)) {
      Object.entries(cleanParams.customFieldFilters).forEach(
        ([key, values]) => {
          urlParams.append('customFieldFilters', `${key}:${values}`);
        }
      );
    }

    const response = await fetchApi.post(
      `${API_PATH.mobi.addSalesOrder}?${urlParams}`,
      payload
    );
    const data = response.data;
    if ([200, 201].indexOf(data.statusCode) === -1) {
      throw new Error(data.message);
    }
    return data;
  } catch (error: unknown) {
    return Promise.reject(error);
  }
};

export const getOrders = async (params: TParamsGetOrders) => {
  try {
    const cleanParams = cleanObject(params);
    const urlParams = new URLSearchParams(cleanParams).toString();

    const response = await fetchApi(`${API_PATH.mobi.orders}?${urlParams}`);
    return response.data?.data?.orders || [];
  } catch (error) {
    return Promise.reject(error);
  }
};

export const cancelOrder = async (payload: TPayloadCancelOrder) => {
  try {
    const response = await fetchApi.put(
      `${API_PATH.mobi.cancelOrders.replace(':id', payload.id)}`,
      { reason: payload.reason }
    );
    const data = response?.data;
    if ([200, 201].indexOf(data.statusCode) === -1) {
      throw new Error(data.message);
    }
    return data?.data || null;
  } catch (error: unknown) {
    return Promise.reject(error);
  }
};

export const cancelAssign = async (payload: TPayloadCancelOrder) => {
  try {
    const response = await fetchApi.put(
      `${API_PATH.mobi.cancelOrdersAssignment.replace(':id', payload.id)}`,
      { reason: payload.reason }
    );
    const data = response?.data;
    if ([200, 201].indexOf(data.statusCode) === -1) {
      throw new Error(data.message);
    }
    return data?.data || null;
  } catch (error: unknown) {
    return Promise.reject(error);
  }
};

export const getDataDeliveryNote = async (id: string | undefined) => {
  try {
    const response = await fetchApi(
      `${API_PATH.mobi.orderDeliveryNotes.replace(':id', id || '')}`
    );
    const data = response?.data;
    if ([200, 201].indexOf(data.statusCode) === -1) {
      throw new Error(data.message);
    }
    return data.data || [];
  } catch (error) {
    return Promise.reject(error);
  }
};

export const publishDeliveryNote = async (payload: {
  orderId: string;
  generatePdf?: boolean;
  validationOnly?: boolean;
  isPreview?: boolean;
  deliveryNotes: TPayloadPublishDeliveryNote[];
}) => {
  let endPointUrl = `${API_PATH.mobi.orderDeliveryNotes.replace(
    ':id',
    payload.orderId
  )}`;
  const queryParams: string[] = [];

  if (payload?.generatePdf) {
    queryParams.push(`generatePdf=${payload.generatePdf}`);
    queryParams.push(`validationOnly=${!!payload?.validationOnly}`);
  }

  if (typeof payload.isPreview === 'boolean') {
    queryParams.push(`isPreview=${payload.isPreview}`);
  }

  if (queryParams.length) {
    endPointUrl += `?${queryParams.join('&')}`;
  }

  try {
    const response = await fetchApi.post(
      endPointUrl,
      { deliveryNotes: payload.deliveryNotes },
      {
        withRequestId: true,
      }
    );
    const data = response?.data;
    if ([200, 201].indexOf(data.statusCode) === -1) {
      throw new Error(data.message);
    }
    return data?.data || null;
  } catch (error: unknown) {
    return Promise.reject(error);
  }
};

export const getTravelExpenseCategoriesByOrg = async () => {
  try {
    const response = await fetchApi(
      `${API_PATH.mobi.orderTravelExpenseCategoriesByOrganization}`
    );
    const data = response?.data;
    if ([200, 201].indexOf(data.statusCode) === -1) {
      throw new Error(data.message);
    }
    return data?.data || [];
  } catch (error) {
    return Promise.reject(error);
  }
};

export const getMasterTravelExpenseCategories = async (keyword: string) => {
  try {
    const response = await fetchApi(
      `${API_PATH.mobi.orderTravelExpenseCategories}?keyword=${keyword}`
    );
    const data = response?.data;
    if ([200, 201].indexOf(data.statusCode) === -1) {
      throw new Error(data.message);
    }
    return data?.data || [];
  } catch (error) {
    return Promise.reject(error);
  }
};

export const postNewExpenseCategoryMaster = async (params: {
  label: string;
  labelEng: string;
}) => {
  try {
    const response = await fetchApi.post(
      `${API_PATH.mobi.orderTravelExpenseCategories}`,
      params,
      {
        withRequestId: true,
      }
    );
    const data = response?.data;
    if ([200, 201].indexOf(data.statusCode) === -1) {
      throw new Error(data.message);
    }
    return data?.data || [];
  } catch (error) {
    return Promise.reject(error);
  }
};

export const postNewExpenseCategoryOrg = async (expenseCategoryId: string) => {
  try {
    const response = await fetchApi.post(
      `${API_PATH.mobi.orderTravelExpenseCategoriesByOrganization}`,
      {
        expenseCategoryId,
      },
      {
        withRequestId: true,
      }
    );
    const data = response?.data;
    if ([200, 201].indexOf(data.statusCode) === -1) {
      throw new Error(data.message);
    }
    return data?.data || [];
  } catch (error) {
    return Promise.reject(error);
  }
};

export const postMobiOrderBulk = async (payload: unknown) => {
  try {
    const response = await fetchApi.post(API_PATH.mobi.orderBulk, payload, {
      withRequestId: true,
    });
    const data = response?.data;
    if ([200, 201].indexOf(data.statusCode) === -1) {
      throw new Error(data.message);
    }
    return data?.data ?? data;
  } catch (error: unknown) {
    return Promise.reject(error);
  }
};
